/* PublicMap — Página completa do Mapa Público (rota /mapa-publico).
 * Usa PublicMapLeaflet (React-Leaflet + OpenStreetMap/CartoDB) — interativo.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Filter, TrendingUp, TrendingDown, Minus, RefreshCcw, X } from "lucide-react";
import DataSourceBadge from "@/components/DataSourceBadge";
import PublicMapLeaflet from "@/components/PublicMapLeaflet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const fmtNum = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

export default function PublicMap() {
  const [points, setPoints] = useState([]);
  const [overview, setOverview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [area, setArea] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async (selectedArea) => {
    setLoading(true);
    try {
      const params = selectedArea ? `?area=${encodeURIComponent(selectedArea)}` : "";
      const [m, o, md] = await Promise.all([
        axios.get(`${API}/map${params}`),
        axios.get(`${API}/overview`),
        axios.get(`${API}/metadata`),
      ]);
      setPoints(m.data || []);
      setOverview(o.data);
      setMeta(md.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(area); }, [area]);

  const filtered = useMemo(() => points, [points]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-6" data-testid="public-map-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">Observatório Prospectivo</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0a2342] mt-1">
            Mapa Público de Produção Prospectiva
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Distribuição mundial agregada de publicações monitoradas pelo Projeto Nostradamus.
            Clique em um marcador para ver o detalhamento. Use os controles do mapa para zoom, troca de tema e tela cheia.
          </p>
          {overview && (
            <div className="mt-3 flex items-center gap-2 flex-wrap text-[12px] text-slate-600">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Origem dos dados:</span>
              <DataSourceBadge
                mode={overview.data_source_mode}
                shortLabel={overview.data_source_short_label}
                note={overview.methodology_note}
              />
              {overview.last_updated_at && (
                <span className="font-mono text-[10.5px] text-slate-500">
                  · Atualizado em {fmtDate(overview.last_updated_at)}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => load(area)}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-slate-200 rounded hover:bg-slate-50"
          data-testid="map-refresh"
        >
          <RefreshCcw size={12} /> Atualizar
        </button>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="public-map-kpis">
          <Kpi label="Documentos" value={fmtNum(overview.total_documents)} accent />
          <Kpi label="Países" value={fmtNum(overview.total_countries)} />
          <Kpi label="Últimos 7d" value={fmtNum(overview.total_last_7_days)} />
          <Kpi label="Vetor dominante" value={overview.dominant_area_label || "—"} small />
          <Kpi label="Maior volume" value={overview.top_country || "—"} small />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded p-3 flex flex-wrap items-center gap-2" data-testid="map-filter">
        <Filter size={12} className="text-slate-500" />
        <span className="text-xs text-slate-500">Filtrar por vetor:</span>
        <button
          onClick={() => setArea("")}
          className={`text-[10px] px-2.5 py-1 rounded border transition font-mono uppercase ${
            !area ? "bg-[#0a2342] text-white border-[#0a2342]" : "border-slate-200 text-slate-600 hover:border-amber-400"
          }`}
          data-testid="filter-area-all"
        >
          Todos
        </button>
        {(overview?.vetores || []).map((v) => (
          <button
            key={v.code}
            onClick={() => setArea(v.code)}
            className={`text-[10px] px-2.5 py-1 rounded border transition ${
              area === v.code
                ? "bg-amber-400 text-[#0a2342] border-amber-400 font-semibold"
                : "border-slate-200 text-slate-600 hover:border-amber-400"
            }`}
            data-testid={`filter-area-${v.code}`}
            title={v.label}
          >
            {v.code}
          </button>
        ))}
      </div>

      {/* Mapa interativo (Leaflet) */}
      <PublicMapLeaflet
        points={filtered}
        meta={meta}
        height={620}
        variant="full"
        onCountryClick={(p) => setSelected(p)}
      />

      {loading && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Carregando…</div>
      )}

      {/* Detalhe ao clicar no marcador (sincronizado com painel lateral) */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000] p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white max-w-md w-full rounded-lg shadow-xl border border-slate-200 p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid={`country-card-${selected.country_code}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display font-bold text-2xl text-[#0a2342]">{selected.country}</div>
                <div className="font-mono text-[10px] text-slate-400 mt-1">{selected.country_code}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <Row label="Publicações monitoradas" value={<strong>{fmtNum(selected.total_documents)}</strong>} />
              <Row label="Últimos 7 dias" value={fmtNum(selected.documents_last_7_days)} />
              <Row
                label="Δ vs semana anterior"
                value={
                  <span className="inline-flex items-center gap-1">
                    {selected.weekly_variation > 0 ? <TrendingUp size={12} className="text-emerald-600" /> :
                     selected.weekly_variation < 0 ? <TrendingDown size={12} className="text-rose-600" /> :
                     <Minus size={12} className="text-slate-400" />}
                    {selected.weekly_variation > 0 ? "+" : ""}{selected.weekly_variation}
                  </span>
                }
              />
              <Row
                label="Vetor dominante"
                value={<strong>{selected.dominant_area_label || selected.dominant_area}</strong>}
              />
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Distribuição por vetor</div>
                {Object.entries(selected.area_distribution || {}).sort((a, b) => b[1] - a[1]).map(([code, v]) => (
                  <div key={code} className="flex justify-between text-xs py-0.5">
                    <span className="text-slate-600">{code}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                ))}
              </div>
              <div className="pt-2 text-[10px] font-mono text-slate-400">Atualizado: {fmtDate(selected.last_updated_at)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600 leading-relaxed">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            {overview?.methodology_note ||
              "Os dados exibidos representam monitoramento agregado de publicações científicas, estratégicas e institucionais relacionadas aos vetores de pesquisa do Projeto Nostradamus. As informações são atualizadas periodicamente."}
          </div>
          <DataSourceBadge
            mode={overview?.data_source_mode}
            shortLabel={overview?.data_source_short_label}
            note={overview?.methodology_note}
          />
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, small }) {
  return (
    <div className={`p-4 rounded border ${accent ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display font-bold text-[#0a2342] mt-1 ${small ? "text-base" : "text-2xl"}`}>{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span>{value}</span></div>;
}
